import * as Sentry from '@sentry/react';
import { camelCase } from 'lodash-es';
import type { History } from 'history';

import type { MapBounds } from 'store/slices/map/map.types';
import type { HeadingVariant, PlaceResultType, ProviderResultType } from 'types/common.types';
import { LOCAL_STORAGE_VERSION_NUMBER, PLACE_RESULT_TYPE, PROVIDER_RESULT_TYPE } from './constants';

export { getEnv, getClient } from './selectToken';

export function setLocalStorageExpire<T>(
  key: string,
  value: T,
  daysToExpire: number,
  contentVersion?: number
): void {
  const daysToMilliseconds = 86400000 * daysToExpire;
  const now = new Date();

  const item = {
    value: value,
    version: LOCAL_STORAGE_VERSION_NUMBER,
    contentVersion: contentVersion || null,
    expiration: daysToExpire ? now.getTime() + daysToMilliseconds : null,
  };
  localStorage.setItem(key, JSON.stringify(item));
}

export function getLocalStorageExpire(key: string, contentVersion?: number): unknown | null {
  const itemStr = localStorage.getItem(key);

  if (!itemStr) {
    return null;
  }
  const item = JSON.parse(itemStr);
  const now = new Date();
  // compare the expiry time of the item with the current time
  if (item.expiration && now.getTime() > item.expiration) {
    localStorage.removeItem(key);
    return null;
  }

  if (contentVersion && contentVersion !== item.contentVersion) {
    localStorage.removeItem(key);
    return null;
  }

  if (LOCAL_STORAGE_VERSION_NUMBER !== item.version) {
    localStorage.removeItem(key);
    return null;
  }
  return item.value;
}

export function distance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
  unit: 'K' | 'M' = 'M'
): number {
  if (lat1 === lat2 && lon1 === lon2) {
    return 0;
  }
  const radlat1 = (Math.PI * lat1) / 180;
  const radlat2 = (Math.PI * lat2) / 180;
  const theta = lon1 - lon2;
  const radtheta = (Math.PI * theta) / 180;
  let dist =
    Math.sin(radlat1) * Math.sin(radlat2) +
    Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
  if (dist > 1) {
    dist = 1;
  }
  dist = Math.acos(dist);
  dist = (dist * 180) / Math.PI;
  dist = dist * 60 * 1.1515;
  if (unit === 'K') {
    dist *= 1.609344;
  }
  return +dist.toFixed(2);
}

interface LocationForUrl {
  address1: string;
  city: string;
  state: string;
}

export function buildLocationUrl(location: LocationForUrl, mapsURL: string): string {
  return `${mapsURL}${location.address1} ${location.city} ${location.state}`;
}

export function buildPlaceAddressUrl(completeAddress: string, mapsURL: string): string {
  return `${mapsURL}${completeAddress}`;
}

// @TODO refactor We already have a library - Yup that can do this for us
export function isValidEmail(email: string): boolean {
  const [user, domain] = email.split('@');
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const userRegex = /^[-!#$%&'*+/=?^_`{}|~0-9A-Z]+(\.[-!#$%&'*+/=?^_`{}|~0-9A-Z]+)*$/i.test(user);
  const domainRegex = /((?:[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?\.)+)(?:[A-Z0-9]{2,63})$/i.test(
    domain
  );

  return [emailRegex, userRegex, domainRegex].every(Boolean);
}

// @TODO refactor We already have a library - Yup that can do this for us
export function isValidPhoneNumber(phoneNumber: string): boolean {
  return /^(\+1|1)?\(?(\d{3})\)?[-]?(\d{3})[-]?(\d{4})$/.test(phoneNumber);
}

// bpd places have phone numbers without dashes -- use this function to add dashes at appropriate spot
export function formatPlacePhoneNumber(phoneNumber: string): string {
  if (phoneNumber.match(/[-]/gi)) return phoneNumber;
  const phoneCopy = phoneNumber.split('');
  phoneCopy.splice(3, 0, '-');
  phoneCopy.splice(7, 0, '-');
  return phoneCopy.join('');
}

export function isValidUrl(urlString: string): boolean {
  // From bleach library used in fusion
  const domains =
    'ac ad ae aero af ag ai al am an ao aq ar arpa as asia at au aw ax az ' +
    'ba bb bd be bf bg bh bi biz bj bm bn bo br bs bt bv bw by bz ca cat ' +
    'cc cd cf cg ch ci ck cl cm cn co com coop cr cu cv cx cy cz de dj dk ' +
    'dm do dz ec edu ee eg er es et eu fi fj fk fm fo fr ga gb gd ge gf gg ' +
    'gh gi gl gm gn gov gp gq gr gs gt gu gw gy hk hm hn hr ht hu id ie il ' +
    'im in info int io iq ir is it je jm jo jobs jp ke kg kh ki km kn kp ' +
    'kr kw ky kz la lb lc li lk lr ls lt lu lv ly ma mc md me mg mh mil mk ' +
    'ml mm mn mo mov mobi mp mq mr ms mt mu museum mv mw mx my mz na name nc ne ' +
    'net nf ng ni nl no np nr nu nz om org pa pe pf pg ph pk pl pm pn post ' +
    'pr pro ps pt pw py qa re ro rs ru rw sa sb sc sd se sg sh si sj sk sl ' +
    'sm sn so sr ss st su sv sx sy sz tc td tel tf tg th tj tk tl tm tn to ' +
    'tp tr travel tt tv tw tz ua ug uk us uy uz va vc ve vg vi vn vu wf ws ' +
    'xn xxx ye yt yu za zip zm zw';

  const domainsArray = domains.split(' ');
  const protocolsArray = [
    'ed2k',
    'ftp',
    'ftps',
    'http',
    'https',
    'irc',
    'mailto',
    'news',
    'gopher',
    'nntp',
    'telnet',
    'webcal',
    'xmpp',
    'callto',
    'feed',
    'urn',
    'aim',
    'rsync',
    'tag',
    'ssh',
    'sftp',
    'rtsp',
    'afs',
    'data',
    'file',
  ];

  const urlPattern = new RegExp(
    `${String.raw`\(*\b(?:(?:${protocolsArray.join(
      '|'
    )}):/{{0,3}}(?:(?:\w+:)?\w+@)?)?([\w-]+\.)+(?:${domainsArray.join(
      '|'
    )})(?:\:[0-9]+)?(?!\.\w)\b(?:[/?][^\s\{{\}}\|\\\^\[\]`}\`<>"]*)?`,
    'im'
  );
  return !!urlPattern.test(urlString);
}

export function containsUnsafeCharacters(input: string): boolean {
  return /[<>&]|".*?"|'.*?'|^"$|^'$/gm.test(input);
}

export function containsHtml(input: string): boolean {
  const el = document.createElement('div');
  el.innerHTML = input;

  if (Array.from(el.childNodes).some((node) => node.nodeType === 1)) {
    return true;
  }

  return false;
}

export const checkInvalidTextField = (message: string): string => {
  const trimmedMessage = message.replace(/\s+/g, ' ').trim();
  const words = trimmedMessage.split(' ');
  const invalidUrl = words.some((word) => isValidUrl(word) || isValidEmail(word));
  const invalidHtml = containsHtml(trimmedMessage);
  const invalidCharacters = containsUnsafeCharacters(trimmedMessage);
  // approximate regex used by the azure waf
  const wafApprovedChars = /^[a-zA-Z0-9 .!?'",;:()-]+$/;
  const wafUnapprovedCharacters = words.some((word) => !wafApprovedChars.test(word));
  let errorMessage = '';
  if (invalidUrl) {
    errorMessage = 'Message cannot contain URLs';
  } else if (invalidHtml) {
    errorMessage = 'Message cannot contain HTML';
  } else if (invalidCharacters) {
    errorMessage = "Message cannot contain '<', '>', or '&' or unsafe quotes";
  } else if (wafUnapprovedCharacters && words[0] !== '') {
    errorMessage = 'Message cannot contain invalid characters';
  }
  return errorMessage;
};

// To convert degrees lat and long to miles...
// A degree latitude is ~69 miles anywhere on the globe
// A degree longitude differs based on latitude and can be calculated with this formula:
// length of 1 degree of Longitude = cosine (latitude in decimal degrees) * length of degree (miles) at equator
// hence:
// const milesPerDegreeLatitude = 69.172;
// const milesPerDegreeLongitude = Math.cos( (Math.PI * southCoords[1]) / 180 ) * milesPerDegreeLatitude;
export function getMilageInLatLong(
  lat: number,
  mileage: number
): { mileageLng: number; mileageLat: number } {
  const milesPerDegreeLatitude = 69.172;
  const milesPerDegreeLongitude = Math.cos((Math.PI * lat) / 180) * milesPerDegreeLatitude;

  const mileageLng = mileage / milesPerDegreeLongitude;
  const mileageLat = mileage / milesPerDegreeLatitude;

  return {
    mileageLng,
    mileageLat,
  };
}

export function detectWebGLSupport(): boolean {
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

  return Boolean(gl && gl instanceof WebGLRenderingContext);
}

// @TODO there is duplicate functionality between this function and the isValidCoords function in mapUtils.ts
export const validateLatLong = (lat: number, lng: number): boolean =>
  lat !== undefined &&
  lat !== null &&
  lng !== undefined &&
  lng !== null &&
  Number(lat) >= -90 &&
  Number(lat) <= 90 &&
  Number(lng) >= -180 &&
  Number(lng) <= 180;

export const validateBounds = (bounds: MapBounds) =>
  validateLatLong(bounds?.ne?.latitude, bounds?.ne?.longitude) &&
  validateLatLong(bounds?.sw?.latitude, bounds?.sw?.longitude);

export const parseJSON = <T>(input: string, fallback: T): unknown | T => {
  try {
    return JSON.parse(input);
    // TODO the type safety of this could be better improved.
  } catch (_e) {
    return fallback;
  }
};

/** Added to prepare for when fusion switches to camel case json payloads from snake case
 * This is not a very Typescript friendly function - we should consider removing this in the future given that Fusion already returns camel case
 */
export const mapCamel = function <T>(arr: T): T {
  const camelizeKeys = <U>(obj: U): U => {
    if (Array.isArray(obj)) {
      return obj.map((v) => camelizeKeys(v)) as U;
    }
    if (obj != null && obj.constructor === Object) {
      return Object.keys(obj).reduce(
        (result, key) => ({
          ...result,
          [camelCase(key)]: camelizeKeys((obj as Record<string, unknown>)[key]),
        }),
        {} as U
      );
    }
    return obj;
  };

  return Array.isArray(arr)
    ? (arr.map((element) => camelizeKeys(element)) as T)
    : camelizeKeys(arr);
};

export function getToday({ offsetYear = 0 }: { offsetYear?: number } = {}): string {
  const today = new Date();
  const yyyy = today.getFullYear() + offsetYear;
  let mm: string | number = today.getMonth() + 1; // Months start at 0!
  let dd: string | number = today.getDate();

  if (dd < 10) dd = `0${dd}`;
  if (mm < 10) mm = `0${mm}`;

  return `${yyyy}-${mm}-${dd}`;
}

type DateConstructor = Date | string | number;
export function withinDateRange(
  dateCheck: DateConstructor,
  start: DateConstructor,
  end: DateConstructor,
  report = true
): boolean {
  if (!(start || end) || !dateCheck) {
    console.error('withinDateRange must have a dateCheck and a start or end argument');
    if (report) {
      Sentry.captureException(
        new Error('withinDateRange must have a dateCheck and a start or end argument')
      );
    }
    return false;
  }

  if (![dateCheck, start, end].every((arg) => arg instanceof Date || arg === null)) {
    console.error('withinDateRange arguments must be a valid Date object or null');
    if (report) {
      Sentry.captureException(
        new Error('withinDateRange arguments must be a valid Date object or null')
      );
    }
    return false;
  }

  if (start && end) {
    return dateCheck >= start && dateCheck <= end;
  }
  if (start) {
    return dateCheck >= start;
  }
  return dateCheck <= end;
}

export const isTodayInDateRange = (start: DateConstructor, end: DateConstructor): boolean => {
  if (!start || !end) {
    console.error('isTodayInDateRange must have "start" and "end" arguments');
    return false;
  }
  const startDate = new Date(start);
  const endDate = new Date(end);
  const today = new Date();
  return withinDateRange(today, startDate, endDate, false);
};

// use this when React needs a key and there is no uuid to supply one, basically the same hash function in Java
// taken from https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript
export function hashForKey(param: string | number): number {
  // in some cases param is an integer
  const stringifiedParam = param.toString();
  let hash = 0;
  let chr;
  let i;
  if (stringifiedParam.length === 0) return hash;
  for (i = 0; i < stringifiedParam.length; i += 1) {
    chr = stringifiedParam.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) - hash + chr;
    // eslint-disable-next-line no-bitwise
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
}

/** This function logs a console message only when in a development environment */
export function logDevMessage(message: string, type: 'error' | 'warn' | 'log' = 'error'): void {
  if (process.env.NODE_ENV === 'development') {
    console[type](message);
  }
}

/** Transforms a phone number to a valid E164 US phone number, or null if it can't be transformed successfully */
export function transformToUsaE164(phoneNumber: string): string | null {
  if (!phoneNumber) return null;

  const numbersOnly = phoneNumber.replace(/\D/g, '');
  const matched = numbersOnly.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (matched) {
    return `+1${matched[1]}${matched[2]}${matched[3]}`;
  }

  return null;
}

/** Determines if the result type is a provider or place */
export function determineResultType(result: object = {}): ProviderResultType | PlaceResultType {
  if ('firstName' in result && 'lastName' in result) return PROVIDER_RESULT_TYPE;
  return PLACE_RESULT_TYPE;
}

/** True if the result is a provider type */
export function resultIsProviderType(result: object): boolean {
  return determineResultType(result) === PROVIDER_RESULT_TYPE;
}

/** True if the result is a place type */
export function resultIsPlaceType(result: object): boolean {
  return determineResultType(result) === PLACE_RESULT_TYPE;
}

export const VALID_HEADING_VARIANTS: HeadingVariant[] = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

/** Calculates the next heading level
 * @example
 * const nextHeading = getNextHeadingLevel("h3");
 * console.log(nextHeading); // "h4"
 */
export function getNextHeadingLevel(variant: HeadingVariant): HeadingVariant {
  const currentIndex = VALID_HEADING_VARIANTS.findIndex((el) => el === variant);
  const nextIndex = currentIndex + 1;
  if (currentIndex === -1) {
    logDevMessage(`Unable to find valid variant ${variant}`);
    return variant;
  }

  if (nextIndex >= VALID_HEADING_VARIANTS.length) {
    return variant;
  }

  return VALID_HEADING_VARIANTS[nextIndex];
}

/** Calculates the next heading level
 * @example
 * const prevHeading = getPreviousHeadingLevel("h3");
 * console.log(prevHeading); // "h2"
 */
export function getPreviousHeadingLevel(variant: HeadingVariant): HeadingVariant {
  const currentIndex = VALID_HEADING_VARIANTS.findIndex((el) => el === variant);
  const prevIndex = currentIndex - 1;
  if (currentIndex === -1) {
    logDevMessage(`Unable to find valid variant ${variant}`);
    return variant;
  }

  if (prevIndex < 0) {
    return variant;
  }

  return VALID_HEADING_VARIANTS[prevIndex];
}

/** Shuffles an array of items of any type. Returns a new array with the same items in a random order. https://stackoverflow.com/a/12646864 */
export function shuffleArray<T>(array: T[]): T[] {
  const dupe = [...array];
  // eslint-disable-next-line no-plusplus
  for (let i = dupe.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [dupe[i], dupe[j]] = [dupe[j], dupe[i]];
  }
  return dupe;
}

/** Returns tier designation content object with `text` and `tooltipMessage` properties */
export const getTierDesignationContent = (
  tier: number | string
): { text: string; tooltipMessage: string } | null => {
  if (!tier) return null;

  const tierDesignationContent: {
    [key: number | string]: { text: string; tooltipMessage: string };
  } = {
    1: {
      text: 'Tier 1',
      tooltipMessage:
        'Using a Tier 1 (Value) provider at a Tier 1 location may result in lower costs to you',
    },
    2: {
      text: 'Tier 2',
      tooltipMessage: 'Using a Tier 2 provider may result in higher costs to you',
    },
    'Non-Tiered': {
      text: 'Non-Tiered',
      tooltipMessage: 'Using a Non-Tiered provider may result in no cost to you.',
    },
    'Non-Tier': {
      text: 'Non-Tiered',
      tooltipMessage: 'Using a Non-Tiered provider may result in no cost to you.',
    },
  };

  return tierDesignationContent[tier] || null;
};

/** Takes an array of objects with "group" property on each obj, sorts them by group property according to the order of groups in the second parameter "groupOrder"  */
export const sortByGroup = (
  options: { group: string }[],
  groupOrder: string[]
): { group: string }[] =>
  options.sort((a, b) => {
    const indexA = groupOrder.indexOf(a.group);
    const indexB = groupOrder.indexOf(b.group);

    // Handle cases where the group is not found in the expectedOrder array
    if (indexA === -1) return 1; // move a to the end
    if (indexB === -1) return -1; // move b to the end

    return indexA - indexB;
  });

/**
 * Returns 9 results from autocomplete suggestions.
 * 1. Gets all suggestions from either "place" or "provider" type care categories
 * 2. If total is less than 9, returns map with the full count from each category
 * 3. If each category has 3 or more suggestions, returns value of 3 for each category
 * 4. In other cases, selects 9 suggestions as evenly among the categories as possible
 *
 * @param {Object[]} suggestionMap autocomplete suggestions mapped to their care category, as defined in selectSearch
 * @param {String[]} keys keys from suggestionMap to select suggestions from
 * @returns object with keys from 'keys' property and values of Int (example: { provider: 3, service: 1, specialty: 0})
 */
export const select9 = <T>(
  suggestionMap: Record<string, T[]>,
  keys: string[]
): Record<string, number> => {
  const countMap: Record<string, number> = {};
  const counts = keys.map((key) => suggestionMap[key]?.length);
  let total = counts.reduce((a, b) => a + b, 0);

  // total count <= 9
  if (total <= 9) {
    keys.forEach((key) => {
      countMap[key] = suggestionMap[key]?.length || 0;
    });
    return countMap;
  }

  // each category has 3 or more
  if (counts.every((i) => i >= 3)) {
    keys.forEach((key) => {
      countMap[key] = 3;
    });
    return countMap;
  }

  // when some categories have >3 and others <3, send a total of 9 spread as evenly as possible
  while (total > 9) {
    const max = Math.max(...counts); // find the highest count value
    counts[counts.indexOf(max)] = max - 1; // reduce the first element with the highest count by 1
    // eslint-disable-next-line no-plusplus
    total--;
  }
  keys.forEach((key, i) => {
    countMap[key] = counts[i];
  });
  return countMap;
};

export const isNotNullish = (val: unknown): boolean => val !== null && val !== undefined;

export const showChatBanner = (location: URL, isMobileScreen: boolean): boolean => {
  const isBaseRoute = location.pathname === '/' || location.pathname.startsWith('/results');
  const isModalRoute =
    location.pathname.startsWith('/specialties') || location.pathname.startsWith('/ai-search');

  return isBaseRoute || (!isMobileScreen && isModalRoute);
};

/**
 * Removes all instances of specified characters from the input string.
 *
 * @example
 * stripCharacters("hello world!", ['l', '!', ' ']); // Returns "heoword"
 */
export function stripCharacters(inputString: string, charsToRemove: string[] = []): string {
  // Create a regular expression that matches any character in charsToRemove
  const pattern = new RegExp(`[${charsToRemove.map((c) => '\\'.concat(c)).join('')}]`, 'g');
  // Replace all matching characters with an empty string
  return inputString.replace(pattern, '');
}

/*
  User data has shown that some users are attempting to use double quotes to do exact searches, similar to how Google performs.

  Because using double quotes does not actually perform exact searches, we want to strip those out to make it clearer to users.

  However, we still need to include single quotes because it could be in a specialty or hospital name. Example: Children's Hospital or Parkinson's Disease
*/
export function sanitizeSearchText(searchText: string): string {
  const charsToRemove = ['"'];
  return stripCharacters(searchText, charsToRemove);
}

/** Function used to determine if the app is being run in the context of an iframe. */
export function isEmbedded(): boolean {
  return window.parent !== window.self;
}

/** Opens a URL in a new tab with noopener and noreferrer */
export function openInNewTab(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** Opens a URL in this tab with noopener and noreferrer */
export function openInSameTab(url: string): void {
  window.open(url, '_self', 'noopener,noreferrer');
}

/** Opens a URL in an external tab if not embedded, otherwise navigates to the intended route */
export function openTab(url: string, history: History): void {
  if (isEmbedded()) {
    history.push(url); // opens in same tab
  } else {
    openInNewTab(url); // opens in new tab
  }
}

export function showInMaps(location: LocationForUrl, mapsURL: string): void {
  openInNewTab(buildLocationUrl(location, mapsURL));
}

export const addSpaceToSlashedWord = (word: string): string => word.replace(/\//g, '/ ');
